import api from './api';
import { downloadFromApi } from './download';

/**
 * Location ENTRANTE (lot 018): la société prend en location des camions chez
 * des transporteurs. Chemins et formes de réponse : `backend/app/api/v1/inbound_rentals.py`
 * et la spec 018 (location entrante).
 *
 * Les montants ne sont jamais calculés ici: l'écran affiche ce que le serveur renvoie.
 * Un rôle sans droit aux montants reçoit des contrats sans tarifs (champs absents).
 */

const BASE = '/inbound-rentals';

const unwrap = (response) => response?.data?.data ?? response?.data;

/** Règle d'un mois `AAAA-MM` à partir d'une date. */
export const moisDe = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mois}`;
};

/** Le mois précédent `AAAA-MM`: celui qu'on règle d'ordinaire. */
export const moisPrecedent = (aujourdhui = new Date()) =>
  moisDe(new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() - 1, 1));

/** Décale un mois `AAAA-MM` (`delta` = -1 pour le précédent, +1 pour le suivant). */
export const decalerMois = (mois, delta) => {
  const [annee, numero] = String(mois).split('-').map(Number);
  if (!annee || !numero) return mois;
  return moisDe(new Date(annee, numero - 1 + delta, 1));
};

/** Retire les champs vides d'un corps de requête (un champ absent garde sa valeur côté serveur). */
const sansVides = (corps) =>
  Object.fromEntries(Object.entries(corps).filter(([, valeur]) => valeur !== undefined));

export const inboundRentalsAPI = {
  // --- Contrats ---------------------------------------------------------------
  async list(params = {}) {
    const response = await api.get(BASE, { params });
    return {
      items: response?.data?.data || [],
      total: response?.data?.pagination?.total ?? (response?.data?.data || []).length,
      pagination: response?.data?.pagination || null,
    };
  },
  async get(contractId) {
    return unwrap(await api.get(`${BASE}/${contractId}`));
  },
  async create(data) {
    return unwrap(await api.post(BASE, sansVides(data)));
  },
  async update(contractId, data) {
    return unwrap(await api.patch(`${BASE}/${contractId}`, sansVides(data)));
  },
  async remove(contractId) {
    return unwrap(await api.delete(`${BASE}/${contractId}`));
  },
  async activate(contractId) {
    return unwrap(await api.post(`${BASE}/${contractId}/activate`));
  },
  async close(contractId, { endDate, reason } = {}) {
    return unwrap(
      await api.post(`${BASE}/${contractId}/close`, {
        end_date: endDate || null,
        reason: reason || null,
      }),
    );
  },

  // --- Camions du contrat ------------------------------------------------------
  async addTruck(contractId, data) {
    return unwrap(await api.post(`${BASE}/${contractId}/trucks`, sansVides(data)));
  },
  async updateTruck(lineId, data) {
    return unwrap(await api.patch(`${BASE}/trucks/${lineId}`, sansVides(data)));
  },
  async removeTruck(lineId) {
    return unwrap(await api.delete(`${BASE}/trucks/${lineId}`));
  },

  // --- Flotte et fiche camion ----------------------------------------------------
  async fleetCheck(truckIds, onDate) {
    return unwrap(
      await api.post(`${BASE}/fleet-check`, { truck_ids: truckIds, on_date: onDate || null }),
    );
  },
  async byTruck(truckId) {
    return unwrap(await api.get(`${BASE}/by-truck/${truckId}`));
  },
  /**
   * Qui supporte cette catégorie de frais pour ce camion à cette date, selon son
   * contrat de location reçue: `{cost_bearer, contract_id, contract_number, lessor_name}`
   * (`contract_id` vide = camion hors contrat, la société paie).
   */
  async getCostBearer(truckId, category, onDate) {
    return unwrap(
      await api.get(`${BASE}/trucks/${truckId}/cost-bearer`, {
        params: { category, on_date: onDate || undefined },
      }),
    );
  },
  async costBearer(truckId, category, onDate) {
    return inboundRentalsAPI.getCostBearer(truckId, category, onDate);
  },

  // --- Montants (direction) --------------------------------------------------------
  async settlements(contractId) {
    return unwrap(await api.get(`${BASE}/${contractId}/settlements`));
  },
  async settlementPreview(contractId, month) {
    return unwrap(await api.get(`${BASE}/${contractId}/settlements/${month}`));
  },
  async saveSettlementDraft(contractId, month) {
    return unwrap(await api.post(`${BASE}/${contractId}/settlements/${month}/draft`));
  },
  async validateSettlement(contractId, month) {
    return unwrap(await api.post(`${BASE}/${contractId}/settlements/${month}/validate`));
  },
  async account(contractId) {
    return unwrap(await api.get(`${BASE}/${contractId}/account`));
  },
  async recordAdvance(contractId, data) {
    return unwrap(await api.post(`${BASE}/${contractId}/advances`, data));
  },
  async statement(contractId, month) {
    return unwrap(await api.get(`${BASE}/${contractId}/statement`, { params: { month } }));
  },
  /** Télécharge le relevé du mois (`pdf` ou `xlsx`); le nom du fichier vient du serveur. */
  async downloadStatement(contractId, month, format = 'pdf') {
    return downloadFromApi(
      `${BASE}/${contractId}/statement/print`,
      { month, format },
      `releve-proprietaire-${month}.${format}`,
      format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf',
    );
  },
};

/** Raccourci: qui paie cette catégorie de frais pour ce camion (voir `inboundRentalsAPI.getCostBearer`). */
export const getCostBearer = (truckId, category, onDate) =>
  inboundRentalsAPI.getCostBearer(truckId, category, onDate);

// --- États de paiement du transport (lot 059) ---------------------------------------------
//
// Contrat réglé « à l'état » (`settlement_method = ETAT`): chaque état porte des
// trajets dépotés. Chemins et formes de réponse: `backend/app/api/v1/inbound_rentals.py`
// et `backend/app/services/transport_statement_service.py`. Direction seulement
// (montants visibles). Toute écriture porte une clé d'idempotence; les montants
// affichés sont ceux du serveur (l'écran de saisie n'en montre qu'un aperçu).

/** Le contenu d'une réponse `{ success, data }`; `data: null` reste `null`, jamais l'enveloppe. */
const contenu = (response) => {
  const corps = response?.data;
  return corps && typeof corps === 'object' && 'data' in corps ? corps.data : corps;
};

const TYPES_IMPRESSION = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

/** Chemin de l'impression d'un état: pour `PrintMenu` (le format part en paramètre). */
export const urlImpressionEtat = (contractId, statementId) =>
  `${BASE}/${contractId}/transport-statements/${statementId}/print`;

export const transportStatementsAPI = {
  /** Sigle de la société et prochain numéro de ligne de facture: `{company_code, invoice_line_next, invoice_line_locked}`. */
  async settings() {
    return contenu(await api.get(`${BASE}/transport-statements/settings`));
  },
  /** Administrateur: `{company_code?, invoice_line_start?}` (le numéro de départ est refusé une fois la série entamée). */
  async updateSettings(data) {
    return contenu(await api.put(`${BASE}/transport-statements/settings`, sansVides(data)));
  },
  /** Administrateur: premier numéro F de ce transporteur (refusé dès qu'un état est numéroté). */
  async setNumbering(contractId, startNumber) {
    return contenu(
      await api.put(`${BASE}/${contractId}/transport-statements/numbering`, {
        statement_start_number: startNumber,
      }),
    );
  },
  /** `{items, numbering: {statement_code, statement_start_number, next_number, locked}, company_code, settlement_method}`. */
  async list(contractId) {
    return contenu(await api.get(`${BASE}/${contractId}/transport-statements`));
  },
  /** `{trips, charges, advances, defaults}`; avec `statementId`, les choix de ce brouillon sont marqués. */
  async eligible(contractId, statementId) {
    return contenu(
      await api.get(`${BASE}/${contractId}/transport-statements/eligible-trips`, {
        params: statementId ? { statement_id: statementId } : undefined,
      }),
    );
  },
  async create(contractId, body) {
    return contenu(await api.post(`${BASE}/${contractId}/transport-statements`, body));
  },
  async get(contractId, statementId) {
    return contenu(await api.get(`${BASE}/${contractId}/transport-statements/${statementId}`));
  },
  async update(contractId, statementId, body) {
    return contenu(await api.patch(`${BASE}/${contractId}/transport-statements/${statementId}`, body));
  },
  async validate(contractId, statementId, idempotencyKey) {
    return contenu(
      await api.post(`${BASE}/${contractId}/transport-statements/${statementId}/validate`, {
        idempotency_key: idempotencyKey,
      }),
    );
  },
  /** Le motif est obligatoire pour annuler un état validé. */
  async cancel(contractId, statementId, { idempotencyKey, reason } = {}) {
    return contenu(
      await api.post(`${BASE}/${contractId}/transport-statements/${statementId}/cancel`, {
        idempotency_key: idempotencyKey,
        reason: reason || null,
      }),
    );
  },
  /** Télécharge l'état (`pdf` ou `xlsx`); le nom du fichier vient du serveur. */
  async download(contractId, statementId, format = 'pdf') {
    return downloadFromApi(
      urlImpressionEtat(contractId, statementId),
      { format },
      `etat-paiement-transport.${format}`,
      TYPES_IMPRESSION[format],
    );
  },
};

export default inboundRentalsAPI;
